import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { InlineError } from "@/components/ui/inline-error";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from '@tanstack/react-query';
import { invalidateChantier } from '@/lib/queryInvalidation';
import { Calendar as CalendarIcon, Calculator, AlertTriangle, RefreshCw, Users, Clock, ChevronDown, ChevronRight, User, Pause } from "lucide-react";
import { format, addDays, eachDayOfInterval } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { EquipeIcon } from "@/components/equipe/EquipeIcon";
import WeekendConfirmDialog, { isWeekend } from "@/components/ui/WeekendConfirmDialog";
import {
  DayPeriodSelector,
  SelectedWorkDaysMap,
  convertWorkDaysToJsonb,
  convertJsonbToWorkDays,
  generateAutoSelection,
  calculateTotalSelectedDays
} from "@/components/ui/DayPeriodSelector";
import { z } from "zod";
import { isValidUUID } from "@/types/schemas";
import { calculateJoursOuvres } from "@/lib/heures-utils";

// === SCHEMA DE VALIDATION AFFECTATION ===
const affectationSubmitSchema = z.object({
  chantier_id: z.string().refine(isValidUUID, "ID chantier invalide"),
  membre_equipe_id: z.string().refine(isValidUUID, "ID membre invalide").optional().nullable(),
  equipe_id: z.string().refine(isValidUUID, "ID equipe invalide").optional().nullable(),
  date_debut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format date invalide"),
  date_fin: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format date invalide"),
  jours_travailles: z.number().positive().max(365, "Maximum 365 jours"),
  heures_par_jour: z.number().positive().max(24, "Maximum 24h/jour"),
  taux_horaire_specifique: z.number().positive().max(1000).optional().nullable(),
  charges_patronales_pct: z.number().min(0).max(100).optional().nullable(),
  notes: z.string().max(500, "500 caracteres max").optional().nullable(),
}).refine(
  data => data.membre_equipe_id || data.equipe_id,
  { message: "Un membre ou une equipe doit etre selectionne" }
);

interface AffectationFormProps {
  chantierId: string;
  entrepriseId: string;
  affectation?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const AffectationForm = ({ chantierId, entrepriseId, affectation, onSuccess, onCancel }: AffectationFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [membres, setMembres] = useState<any[]>([]);
  const [equipes, setEquipes] = useState<any[]>([]);
  const [chantier, setChantier] = useState<any>(null);
  // Affectations actives par équipe (pour afficher sur quel chantier chaque équipe est affectée)
  const [affectationsEquipes, setAffectationsEquipes] = useState<Record<string, { chantiers: string[]; dateFin: string }>>({});
  // Affectations actives par membre individuel (pour afficher sur quel chantier chaque membre est affecté)
  const [affectationsMembres, setAffectationsMembres] = useState<Record<string, { chantiers: string[]; chantierId: string; dateFin: string; affectationId: string }>>({});
  // Détails complets des affectations pour détecter les chevauchements
  const [affectationsDetails, setAffectationsDetails] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<"membre" | "equipe">(affectation?.membre_equipe_id ? "membre" : "equipe");
  const [formData, setFormData] = useState({
    membre_equipe_id: affectation?.membre_equipe_id || "",
    equipe_id: affectation?.equipe_id || "",
    date_debut: affectation?.date_debut ? new Date(affectation.date_debut) : new Date(),
    date_fin: affectation?.date_fin ? new Date(affectation.date_fin) : new Date(),
    jours_travailles: affectation?.jours_travailles || 1,
    heures_par_jour: affectation?.heures_par_jour || 8,
    taux_horaire_specifique: affectation?.taux_horaire_specifique || "",
    useTauxSpecifique: !!affectation?.taux_horaire_specifique,
    notes: affectation?.notes || "",
  });

  const [calculatedCost, setCalculatedCost] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  // États pour gérer le chevauchement
  const [showRepartitionModal, setShowRepartitionModal] = useState(false);
  const [heuresChantierActuel, setHeuresChantierActuel] = useState(4);
  const [heuresAutreChantier, setHeuresAutreChantier] = useState(4);
  const [loadingRotation, setLoadingRotation] = useState(false);
  // Dates pour travail simultané équipe
  const [dateDebutSimultane, setDateDebutSimultane] = useState<Date>(new Date());
  const [dateFinSimultane, setDateFinSimultane] = useState<Date>(new Date());

  // États pour le modal de rotation avec distinction heures jour 1 / jours suivants
  const [showRotationModal, setShowRotationModal] = useState(false);
  const [heuresAncienChantier, setHeuresAncienChantier] = useState(4); // Heures sur l'ancien chantier aujourd'hui
  const [heuresJour1Rotation, setHeuresJour1Rotation] = useState(4); // Heures sur le nouveau chantier aujourd'hui
  const [heuresJoursSuivantsRotation, setHeuresJoursSuivantsRotation] = useState(8);
  const [dateDebutRotation, setDateDebutRotation] = useState<Date>(new Date()); // Date début nouvelle affectation
  const [dateFinRotation, setDateFinRotation] = useState<Date>(new Date()); // Date fin nouvelle affectation

  // États pour le modal de rotation/travail simultané des membres individuels
  const [showRotationModalMembre, setShowRotationModalMembre] = useState(false);
  const [showRepartitionModalMembre, setShowRepartitionModalMembre] = useState(false);
  const [heuresAncienChantierMembre, setHeuresAncienChantierMembre] = useState(4);
  const [heuresJour1RotationMembre, setHeuresJour1RotationMembre] = useState(4);
  const [heuresJoursSuivantsRotationMembre, setHeuresJoursSuivantsRotationMembre] = useState(8);
  const [dateDebutRotationMembre, setDateDebutRotationMembre] = useState<Date>(new Date());
  const [dateFinRotationMembre, setDateFinRotationMembre] = useState<Date>(new Date());
  const [heuresChantierActuelMembre, setHeuresChantierActuelMembre] = useState(4);
  const [heuresAutreChantierMembre, setHeuresAutreChantierMembre] = useState(4);
  // Dates pour travail simultané membre
  const [dateDebutSimultaneMembre, setDateDebutSimultaneMembre] = useState<Date>(new Date());
  const [dateFinSimultaneMembre, setDateFinSimultaneMembre] = useState<Date>(new Date());

  // États pour les sections collapsibles du dropdown membres (repliées par défaut)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    sur_chantier: false,
    en_rotation: false,
    disponible: false,
  });

  // État pour le popup de confirmation weekend
  const [showWeekendDialog, setShowWeekendDialog] = useState(false);
  const [pendingDateFin, setPendingDateFin] = useState<Date | null>(null);
  const [inclureWeekendFin, setInclureWeekendFin] = useState(false);

  // États pour le modal de sélection des demi-journées
  const [showDayPeriodModal, setShowDayPeriodModal] = useState(false);
  const [selectedWorkDays, setSelectedWorkDays] = useState<SelectedWorkDaysMap>(new Map());
  const [hasConfirmedDaySelection, setHasConfirmedDaySelection] = useState(false);
  const [pendingDayPeriodModal, setPendingDayPeriodModal] = useState(false);

  // Flag pour éviter que l'auto-calcul écrase les valeurs utilisateur
  const [hasUserModifiedJours, setHasUserModifiedJours] = useState(false);

  // Helper pour rafraîchir les métriques du chantier après modification des affectations
  // Ceci déclenche la mise à jour realtime pour la sidebar
  const refreshChantierMetrics = async (chantierIdToRefresh: string) => {
    try {
      const { data: calcData, error: calcError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierIdToRefresh });

      if (calcError) {
        console.error('[AffectationForm] Erreur calcul métriques:', calcError);
        return;
      }

      if (calcData) {
        // Stocker pour déclencher le realtime (upsert)
        await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierIdToRefresh,
            metrics: calcData,
            updated_at: new Date().toISOString()
          });
        window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
          detail: { chantierId: chantierIdToRefresh }
        }));
        // Invalider le cache React Query
        invalidateChantier(queryClient, entrepriseId, chantierIdToRefresh);
      }
    } catch (error) {
      console.error('[AffectationForm] Exception rafraîchissement métriques:', error);
    }
  };

  // Wrapper qui rafraîchit les métriques puis appelle onSuccess
  const handleSuccessWithMetricsRefresh = async (additionalChantierIds?: string[]) => {
    // Rafraîchir les métriques du chantier actuel
    await refreshChantierMetrics(chantierId);

    // Rafraîchir les métriques des autres chantiers impactés (ex: rotation)
    if (additionalChantierIds) {
      for (const id of additionalChantierIds) {
        if (id !== chantierId) {
          await refreshChantierMetrics(id);
        }
      }
    }

    onSuccess();
  };

  // Calculer les jours ouvrés pour la rotation membre
  const joursOuvresRotationMembre = React.useMemo(() => {
    if (!dateDebutRotationMembre || !dateFinRotationMembre || dateDebutRotationMembre > dateFinRotationMembre) return 0;
    return calculateJoursOuvres(dateDebutRotationMembre, dateFinRotationMembre);
  }, [dateDebutRotationMembre, dateFinRotationMembre]);

  // Calculer les jours ouvrés pour la rotation
  const joursOuvresRotation = React.useMemo(() => {
    if (!dateDebutRotation || !dateFinRotation || dateDebutRotation > dateFinRotation) return 0;
    return calculateJoursOuvres(dateDebutRotation, dateFinRotation);
  }, [dateDebutRotation, dateFinRotation]);

  // Calculer les jours ouvrés pour le travail simultané équipe
  const joursOuvresSimultane = React.useMemo(() => {
    if (!dateDebutSimultane || !dateFinSimultane || dateDebutSimultane > dateFinSimultane) return 0;
    return calculateJoursOuvres(dateDebutSimultane, dateFinSimultane);
  }, [dateDebutSimultane, dateFinSimultane]);

  // Calculer les jours ouvrés pour le travail simultané membre
  const joursOuvresSimultaneMembre = React.useMemo(() => {
    if (!dateDebutSimultaneMembre || !dateFinSimultaneMembre || dateDebutSimultaneMembre > dateFinSimultaneMembre) return 0;
    return calculateJoursOuvres(dateDebutSimultaneMembre, dateFinSimultaneMembre);
  }, [dateDebutSimultaneMembre, dateFinSimultaneMembre]);

  // Détecter les chevauchements d'équipe COMPLET (tous les membres sont en conflit)
  // Ne se déclenche que si AUCUN membre n'est disponible
  const chevauchementEquipe = React.useMemo(() => {
    if (selectedType !== "equipe" || !formData.equipe_id || !formData.date_debut || !formData.date_fin) {
      return null;
    }

    const dateDebut = format(formData.date_debut, "yyyy-MM-dd");
    const dateFin = format(formData.date_fin, "yyyy-MM-dd");

    // Vérifier combien de membres sont en conflit
    const membresEquipe = (membres || []).filter(m => m.equipe_id === formData.equipe_id);
    if (membresEquipe.length === 0) return null;

    // Compter les membres en conflit
    let membresEnConflit = 0;
    let premierChantierConflit: any = null;

    for (const membre of membresEquipe) {
      const affectationConflictuelle = affectationsDetails.find(aff => {
        if (aff.membre_equipe_id !== membre.id) return false;
        if (aff.chantier_id === chantierId) return false;
        return aff.date_debut <= dateFin && dateDebut <= aff.date_fin;
      });

      if (affectationConflictuelle) {
        membresEnConflit++;
        if (!premierChantierConflit) {
          premierChantierConflit = affectationConflictuelle;
        }
      }
    }

    // Chevauchement complet = TOUS les membres sont en conflit
    if (membresEnConflit === membresEquipe.length && premierChantierConflit) {
      const chantierInfo = premierChantierConflit.chantiers as any;
      return {
        affectationId: premierChantierConflit.id,
        chantierId: premierChantierConflit.chantier_id,
        chantierNom: chantierInfo?.reference_chantier || chantierInfo?.nom_chantier || "Chantier inconnu",
        dateDebut: premierChantierConflit.date_debut,
        dateFin: premierChantierConflit.date_fin,
        heuresParJour: premierChantierConflit.heures_par_jour || 8,
      };
    }

    return null;
  }, [selectedType, formData.equipe_id, formData.date_debut, formData.date_fin, affectationsDetails, chantierId, membres]);

  // Détecter les chevauchements pour un membre individuel
  const chevauchementMembre = React.useMemo(() => {
    if (selectedType !== "membre" || !formData.membre_equipe_id || !formData.date_debut || !formData.date_fin) {
      return null;
    }

    const dateDebut = format(formData.date_debut, "yyyy-MM-dd");
    const dateFin = format(formData.date_fin, "yyyy-MM-dd");

    // Chercher une affectation existante pour ce membre qui chevauche les dates
    const affectationExistante = affectationsDetails.find(aff => {
      if (aff.membre_equipe_id !== formData.membre_equipe_id) return false;
      // Ne pas considérer le chantier actuel comme un chevauchement
      if (aff.chantier_id === chantierId) return false;

      // Vérifier le chevauchement de dates
      const chevauche = aff.date_debut <= dateFin && dateDebut <= aff.date_fin;
      return chevauche;
    });

    if (affectationExistante) {
      const chantierInfo = affectationExistante.chantiers as any;
      return {
        affectationId: affectationExistante.id,
        chantierId: affectationExistante.chantier_id,
        chantierNom: chantierInfo?.reference_chantier || chantierInfo?.nom_chantier || "Chantier inconnu",
        dateDebut: affectationExistante.date_debut,
        dateFin: affectationExistante.date_fin,
        heuresParJour: affectationExistante.heures_par_jour || 8,
      };
    }

    return null;
  }, [selectedType, formData.membre_equipe_id, formData.date_debut, formData.date_fin, affectationsDetails, chantierId]);

  // Calculer la disponibilité par équipe (X/Y membres disponibles)
  const disponibiliteParEquipe = React.useMemo(() => {
    if (!formData.date_debut || !formData.date_fin) {
      return {};
    }

    const dateDebut = format(formData.date_debut, "yyyy-MM-dd");
    const dateFin = format(formData.date_fin, "yyyy-MM-dd");

    const result: Record<string, {
      total: number;
      disponibles: number;
      membresEnConflit: Array<{
        membreId: string;
        membreNom: string;
        chantierNom: string;
        dateFin: string;
      }>;
    }> = {};

    (equipes || []).forEach(equipe => {
      const membresEquipe = (membres || []).filter(m => m.equipe_id === equipe.id);
      const membresEnConflit: Array<{
        membreId: string;
        membreNom: string;
        chantierNom: string;
        dateFin: string;
      }> = [];

      membresEquipe.forEach(membre => {
        // Vérifier si ce membre a une affectation qui chevauche les dates demandées
        const affectationConflictuelle = affectationsDetails.find(aff => {
          if (aff.membre_equipe_id !== membre.id) return false;
          // Ne pas considérer le chantier actuel comme un conflit
          if (aff.chantier_id === chantierId) return false;
          // Vérifier le chevauchement de dates
          return aff.date_debut <= dateFin && dateDebut <= aff.date_fin;
        });

        if (affectationConflictuelle) {
          const chantierInfo = affectationConflictuelle.chantiers as any;
          membresEnConflit.push({
            membreId: membre.id,
            membreNom: `${membre.prenom} ${membre.nom}`,
            chantierNom: chantierInfo?.reference_chantier || chantierInfo?.nom_chantier || "Chantier inconnu",
            dateFin: affectationConflictuelle.date_fin,
          });
        }
      });

      result[equipe.id] = {
        total: membresEquipe.length,
        disponibles: membresEquipe.length - membresEnConflit.length,
        membresEnConflit,
      };
    });

    return result;
  }, [equipes, membres, formData.date_debut, formData.date_fin, affectationsDetails, chantierId]);

  // Détecter les chevauchements partiels d'équipe (certains membres occupés, pas tous)
  const chevauchementEquipePartiel = React.useMemo(() => {
    if (selectedType !== "equipe" || !formData.equipe_id || !formData.date_debut || !formData.date_fin) {
      return null;
    }

    const dispo = disponibiliteParEquipe[formData.equipe_id];
    if (!dispo || !dispo.membresEnConflit) return null;

    // Pas de conflit partiel si tous les membres sont dispo ou tous en conflit
    if (dispo.membresEnConflit.length === 0 || dispo.disponibles === 0) {
      return null;
    }

    // Calculer la date de libération (date la plus tardive parmi les conflits)
    const dateLiberation = dispo.membresEnConflit.reduce((latest, conflit) => {
      return conflit.dateFin > latest ? conflit.dateFin : latest;
    }, dispo.membresEnConflit[0]?.dateFin || '');

    return {
      equipeId: formData.equipe_id,
      total: dispo.total,
      disponibles: dispo.disponibles,
      membresEnConflit: dispo.membresEnConflit,
      dateLiberation,
    };
  }, [selectedType, formData.equipe_id, formData.date_debut, formData.date_fin, disponibiliteParEquipe]);

  useEffect(() => {
    loadData();
  }, [entrepriseId, chantierId]);

  // Pré-remplir les dates ET les jours travaillés depuis le chantier (seulement en mode création)
  // IMPORTANT: Tout dans un seul useEffect pour éviter les conditions de course
  // Priorité pour jours_travailles : jours_details > duree_estimee_jours > calcul automatique
  useEffect(() => {
    if (chantier && !affectation?.id) {
      // Extraire les dates du chantier
      const dateFin = chantier.date_fin_prevue || chantier.date_fin_prevu || chantier.date_fin_estimee || chantier.date_fin;
      const dateDebutChantier = chantier.date_debut_prevue || chantier.date_debut_prevu || chantier.date_debut_reelle || chantier.date_debut;

      if (dateFin) {
        const today = new Date();
        const fin = new Date(dateFin);
        const debut = today > fin && dateDebutChantier ? new Date(dateDebutChantier) : today;
        const joursOuvresCalcules = calculateJoursOuvres(debut, fin);

        // Déterminer la valeur de jours_travailles selon la priorité
        let joursTravailles = Math.max(1, joursOuvresCalcules);
        let shouldBlockAutoCalc = false;

        // Priorité 1 : jours_details (sélection précise des demi-journées)
        if (chantier.jours_details && Array.isArray(chantier.jours_details) && chantier.jours_details.length > 0) {
          const workDaysMap = convertJsonbToWorkDays(chantier.jours_details);
          if (workDaysMap.size > 0) {
            setSelectedWorkDays(workDaysMap);
            setHasConfirmedDaySelection(true);
            joursTravailles = calculateTotalSelectedDays(workDaysMap);
            shouldBlockAutoCalc = true;
          }
        }
        // Priorité 2 : duree_estimee_jours (fallback si pas de jours_details)
        else if (chantier.duree_estimee_jours && chantier.duree_estimee_jours > 0) {
          joursTravailles = chantier.duree_estimee_jours;
          shouldBlockAutoCalc = true;
        }

        // Mettre à jour le formulaire en une seule fois
        setFormData(prev => ({
          ...prev,
          date_debut: debut,
          date_fin: fin,
          jours_travailles: joursTravailles,
        }));

        // Bloquer le recalcul automatique si on a utilisé une valeur du chantier
        if (shouldBlockAutoCalc) {
          setHasUserModifiedJours(true);
        }
      }
    }
  }, [chantier, affectation?.id]);

  // Calcul automatique des jours ouvrés quand les dates changent
  // Ne s'exécute PAS si l'utilisateur a modifié manuellement jours_travailles
  useEffect(() => {
    // Ne pas recalculer si l'utilisateur a modifié manuellement
    if (hasUserModifiedJours) return;

    if (formData.date_debut && formData.date_fin && formData.date_debut <= formData.date_fin) {
      const jours = calculateJoursOuvres(formData.date_debut, formData.date_fin, inclureWeekendFin);
      setFormData(prev => ({
        ...prev,
        jours_travailles: Math.max(1, jours),
      }));
    }
  }, [formData.date_debut, formData.date_fin, inclureWeekendFin, hasUserModifiedJours]);

  // useEffect pour ouvrir le modal DayPeriodSelector de manière fiable
  // S'ouvre dès qu'il y a un écart entre jours saisis et jours ouvrés
  useEffect(() => {
    if (pendingDayPeriodModal && !isNaN(formData.jours_travailles) && formData.date_debut && formData.date_fin) {
      const joursOuvres = calculateJoursOuvres(formData.date_debut, formData.date_fin, inclureWeekendFin);
      const ecart = formData.jours_travailles - joursOuvres;

      // Ouvrir le modal si écart non nul (positif = weekends, négatif = moins de jours)
      if (ecart !== 0) {
        setShowDayPeriodModal(true);
        setPendingDayPeriodModal(false);
      } else {
        // Pas d'écart, annuler le pending
        setPendingDayPeriodModal(false);
      }
    }
  }, [pendingDayPeriodModal, formData.jours_travailles, formData.date_debut, formData.date_fin, inclureWeekendFin]);

  // Gérer le changement de date de fin avec vérification weekend
  const handleDateFinChange = (newDateFin: Date | undefined) => {
    if (!newDateFin) return;
    if (isWeekend(newDateFin)) {
      // La date tombe un weekend, demander confirmation
      setPendingDateFin(newDateFin);
      setShowWeekendDialog(true);
    } else {
      // Jour de semaine, appliquer directement
      setFormData(prev => ({ ...prev, date_fin: newDateFin }));
      setInclureWeekendFin(false);
    }
  };

  // Callback du dialogue weekend
  const handleWeekendConfirm = (inclure: boolean) => {
    setInclureWeekendFin(inclure);
    if (pendingDateFin) {
      setFormData(prev => ({ ...prev, date_fin: pendingDateFin }));
    }
    setShowWeekendDialog(false);
    setPendingDateFin(null);
  };

  useEffect(() => {
    calculateCost();
  }, [formData, membres, equipes, selectedType]);

  const loadData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      const [membresRes, equipesRes, chantierRes, affectationsEquipesRes, affectationsMembresRes] = await Promise.all([
        supabase
          .from("membres_equipe")
          .select("*")
          .eq("entreprise_id", entrepriseId)
          .in("statut", ['sur_chantier', 'en_rotation', 'disponible'])
          .order("nom"),
        supabase
          .from("equipes")
          .select("*")
          .eq("entreprise_id", entrepriseId)
          .order("nom"),
        supabase
          .from("chantiers")
          .select("*")
          .eq("id", chantierId)
          .single(),
        // Charger les affectations actives des équipes (avec heures_par_jour pour la rotation)
        supabase
          .from("affectations_chantiers")
          .select("id, equipe_id, membre_equipe_id, date_debut, date_fin, chantier_id, heures_par_jour, chantiers(reference_chantier, nom_chantier)")
          .not("equipe_id", "is", null)
          .gte("date_fin", today),
        // Charger les affectations actives des membres individuels (avec heures_par_jour pour la rotation)
        supabase
          .from("affectations_chantiers")
          .select("id, equipe_id, membre_equipe_id, date_debut, date_fin, chantier_id, heures_par_jour, chantiers(reference_chantier, nom_chantier)")
          .not("membre_equipe_id", "is", null)
          .gte("date_fin", today),
      ]);

      setMembres(membresRes.data || []);
      setEquipes(equipesRes.data || []);
      setChantier(chantierRes.data);

      // Combiner les affectations équipes et membres pour les détails
      const allAffectations = [
        ...(affectationsEquipesRes.data || []),
        ...(affectationsMembresRes.data || []),
      ];
      // Supprimer les doublons (par id)
      const uniqueAffectations = allAffectations.filter((aff, index, self) =>
        index === self.findIndex(a => a.id === aff.id)
      );
      setAffectationsDetails(uniqueAffectations);

      // Construire un map equipe_id -> { chantiers[], dateFin }
      const affectationsEquipesMap: Record<string, { chantiers: string[]; dateFin: string }> = {};
      if (affectationsEquipesRes.data) {
        for (const aff of affectationsEquipesRes.data) {
          if (aff.equipe_id && aff.chantiers) {
            const chantierInfo = aff.chantiers as any;
            const chantierRef = chantierInfo.reference_chantier || chantierInfo.nom_chantier;

            if (affectationsEquipesMap[aff.equipe_id]) {
              if (!affectationsEquipesMap[aff.equipe_id].chantiers.includes(chantierRef)) {
                affectationsEquipesMap[aff.equipe_id].chantiers.push(chantierRef);
              }
              if (aff.date_fin > affectationsEquipesMap[aff.equipe_id].dateFin) {
                affectationsEquipesMap[aff.equipe_id].dateFin = aff.date_fin;
              }
            } else {
              affectationsEquipesMap[aff.equipe_id] = {
                chantiers: [chantierRef],
                dateFin: aff.date_fin,
              };
            }
          }
        }
      }
      setAffectationsEquipes(affectationsEquipesMap);

      // Construire un map membre_equipe_id -> { chantiers[], chantierId, dateFin, affectationId }
      const affectationsMembresMap: Record<string, { chantiers: string[]; chantierId: string; dateFin: string; affectationId: string }> = {};
      if (affectationsMembresRes.data) {
        for (const aff of affectationsMembresRes.data) {
          if (aff.membre_equipe_id && aff.chantiers) {
            const chantierInfo = aff.chantiers as any;
            const chantierRef = chantierInfo.reference_chantier || chantierInfo.nom_chantier;

            if (affectationsMembresMap[aff.membre_equipe_id]) {
              if (!affectationsMembresMap[aff.membre_equipe_id].chantiers.includes(chantierRef)) {
                affectationsMembresMap[aff.membre_equipe_id].chantiers.push(chantierRef);
              }
              if (aff.date_fin > affectationsMembresMap[aff.membre_equipe_id].dateFin) {
                affectationsMembresMap[aff.membre_equipe_id].dateFin = aff.date_fin;
                affectationsMembresMap[aff.membre_equipe_id].chantierId = aff.chantier_id;
                affectationsMembresMap[aff.membre_equipe_id].affectationId = aff.id;
              }
            } else {
              affectationsMembresMap[aff.membre_equipe_id] = {
                chantiers: [chantierRef],
                chantierId: aff.chantier_id,
                dateFin: aff.date_fin,
                affectationId: aff.id,
              };
            }
          }
        }
      }
      setAffectationsMembres(affectationsMembresMap);
    } catch (error) {
      console.error("Erreur chargement données:", error);
    }
  };

  const calculateCost = () => {
    const { jours_travailles, heures_par_jour, taux_horaire_specifique, useTauxSpecifique, membre_equipe_id, equipe_id } = formData;

    let totalCout = 0;

    if (selectedType === "membre" && membre_equipe_id) {
      const membre = membres.find(m => m.id === membre_equipe_id);
      if (membre) {
        const tauxHoraire = useTauxSpecifique && taux_horaire_specifique
          ? parseFloat(taux_horaire_specifique)
          : membre.taux_horaire;
        const coef = membre.coefficient_charges || 1.85;
        totalCout = jours_travailles * heures_par_jour * tauxHoraire * coef;
      }
    } else if (selectedType === "equipe" && equipe_id) {
      // Pour une équipe, calculer le coût total de tous les membres
      const membresEquipe = (membres || []).filter(m => m.equipe_id === equipe_id);
      totalCout = membresEquipe.reduce((sum, membre) => {
        const tauxHoraire = membre.taux_horaire;
        const coef = membre.coefficient_charges || 1.85;
        const coutMembre = jours_travailles * heures_par_jour * tauxHoraire * coef;
        return sum + coutMembre;
      }, 0);
    }

    setCalculatedCost(totalCout);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validation avant soumission
    if (selectedType === "equipe" && !formData.equipe_id) {
      setValidationError("Veuillez sélectionner une équipe");
      return;
    }
    if (selectedType === "membre" && !formData.membre_equipe_id) {
      setValidationError("Veuillez sélectionner un membre");
      return;
    }

    // Validation UUID des IDs (securite)
    if (!isValidUUID(chantierId)) {
      setValidationError("ID chantier invalide");
      return;
    }
    if (selectedType === "membre" && !isValidUUID(formData.membre_equipe_id)) {
      setValidationError("ID membre invalide");
      return;
    }
    if (selectedType === "equipe" && !isValidUUID(formData.equipe_id)) {
      setValidationError("ID equipe invalide");
      return;
    }

    setLoading(true);

    try {
      if (selectedType === "membre") {
        // Affectation d'un membre individuel
        const membre = membres.find(m => m.id === formData.membre_equipe_id);
        if (!membre) throw new Error("Membre non trouvé");

        // Préparer jours_details si une sélection a été confirmée
        const joursDetails = hasConfirmedDaySelection && selectedWorkDays.size > 0
          ? convertWorkDaysToJsonb(selectedWorkDays)
          : null;

        const dataToSubmit = {
          chantier_id: chantierId,
          membre_equipe_id: formData.membre_equipe_id,
          equipe_id: formData.equipe_id || null,
          date_debut: format(formData.date_debut, "yyyy-MM-dd"),
          date_fin: format(formData.date_fin, "yyyy-MM-dd"),
          jours_travailles: formData.jours_travailles,
          heures_par_jour: formData.heures_par_jour,
          taux_horaire_specifique: formData.useTauxSpecifique && formData.taux_horaire_specifique
            ? parseFloat(formData.taux_horaire_specifique)
            : membre.taux_horaire,
          charges_patronales_pct: membre.charges_patronales,
          notes: formData.notes,
          jours_details: joursDetails,
        };

        // Validation Zod des donnees avant envoi
        const validationResult = affectationSubmitSchema.safeParse(dataToSubmit);
        if (!validationResult.success) {
          const errorMsg = (validationResult.error?.errors || []).map(e => e.message).join(", ") || "Données invalides";
          setValidationError(errorMsg);
          setLoading(false);
          return;
        }

        if (affectation) {
          const { error } = await supabase
            .from("affectations_chantiers")
            .update(dataToSubmit)
            .eq("id", affectation.id);

          if (error) throw error;
          toast({ title: "Affectation modifiée" });
        } else {
          const { error } = await supabase
            .from("affectations_chantiers")
            .insert(dataToSubmit);

          if (error) throw error;
          toast({ title: "Affectation créée" });
        }
      } else {
        // Affectation d'une équipe complète - créer une affectation pour chaque membre
        const membresEquipe = (membres || []).filter(m => m.equipe_id === formData.equipe_id);

        if (membresEquipe.length === 0) {
          throw new Error("Cette équipe n'a aucun membre. Ajoutez des membres à l'équipe d'abord.");
        }

        // Préparer jours_details si une sélection a été confirmée (pour équipe aussi)
        const joursDetailsEquipe = hasConfirmedDaySelection && selectedWorkDays.size > 0
          ? convertWorkDaysToJsonb(selectedWorkDays)
          : null;

        const affectationsToInsert = membresEquipe.map(membre => ({
          chantier_id: chantierId,
          membre_equipe_id: membre.id,
          equipe_id: formData.equipe_id,
          date_debut: format(formData.date_debut, "yyyy-MM-dd"),
          date_fin: format(formData.date_fin, "yyyy-MM-dd"),
          jours_travailles: formData.jours_travailles,
          heures_par_jour: formData.heures_par_jour,
          taux_horaire_specifique: membre.taux_horaire,
          charges_patronales_pct: membre.charges_patronales,
          notes: formData.notes,
          jours_details: joursDetailsEquipe,
        }));

        // Validation Zod de chaque affectation avant envoi
        for (const aff of affectationsToInsert) {
          const validationResult = affectationSubmitSchema.safeParse(aff);
          if (!validationResult.success) {
            const errorMsg = (validationResult.error?.errors || []).map(e => e.message).join(", ") || "Données invalides";
            setValidationError(errorMsg);
            setLoading(false);
            return;
          }
        }

        const { error } = await supabase
          .from("affectations_chantiers")
          .insert(affectationsToInsert);

        if (error) throw error;
        toast({ 
          title: `${membresEquipe.length} membre${membresEquipe.length > 1 ? 's' : ''} affecté${membresEquipe.length > 1 ? 's' : ''}`,
          description: `L'équipe a été affectée avec succès`
        });
      }

      await handleSuccessWithMetricsRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const autoCalculateJours = () => {
    const jours = calculateJoursOuvres(formData.date_debut, formData.date_fin);
    // Reset le flag pour permettre le calcul automatique
    setHasUserModifiedJours(false);
    setFormData({ ...formData, jours_travailles: Math.max(1, jours) });
  };

  // Ouvrir le modal de rotation (au lieu d'exécuter directement)
  const openRotationModal = () => {
    // Initialiser les heures en se basant sur l'affectation existante
    const heuresExistantes = chevauchementEquipe?.heuresParJour || 8;
    setHeuresAncienChantier(heuresExistantes / 2);
    setHeuresJour1Rotation(heuresExistantes / 2);
    setHeuresJoursSuivantsRotation(heuresExistantes);
    // Utiliser les dates du formulaire (pas aujourd'hui pour date_debut)
    setDateDebutRotation(formData.date_debut);
    setDateFinRotation(formData.date_fin);
    setShowRotationModal(true);
  };

  // Mettre à jour les membres mobilisés sur le retard après une rotation
  // 1. Retire le membre de la liste membres_mobilises_ids dans frais_chantier
  // 2. Supprime l'affectation de retard du membre (pour recalculer le coût)
  const updateMembresRetardApresRotation = async (membreId: string, ancienChantierId: string) => {
    try {
      // 1. Chercher les frais de type retard pour l'ancien chantier
      const { data: fraisRetard } = await supabase
        .from('frais_chantier')
        .select('id, membres_mobilises_ids')
        .eq('chantier_id', ancienChantierId)
        .or('type_frais.ilike.%Pénalité%,type_frais.ilike.%retard%,type_frais.ilike.%Coût retard%')
        .maybeSingle();

      if (fraisRetard?.membres_mobilises_ids) {
        const membresMobilises = fraisRetard.membres_mobilises_ids as string[];
        // Retirer le membre de la liste
        const nouveauxMembres = membresMobilises.filter(id => id !== membreId);

        // Ne mettre à jour que si le membre était bien dans la liste
        if (nouveauxMembres.length < membresMobilises.length) {
          await supabase
            .from('frais_chantier')
            .update({ membres_mobilises_ids: nouveauxMembres })
            .eq('id', fraisRetard.id);
        }
      }

      // 2. Supprimer l'affectation de retard du membre
      // (affectation avec notes contenant "Retard chantier")
      // Ceci permet de recalculer correctement le coût du retard
      const { error: deleteError } = await supabase
        .from('affectations_chantiers')
        .delete()
        .eq('chantier_id', ancienChantierId)
        .eq('membre_equipe_id', membreId)
        .like('notes', 'Retard chantier%');

      if (deleteError) {
        console.error('Erreur suppression affectation retard:', deleteError);
      }
    } catch (error) {
      // Ne pas bloquer la rotation si cette mise à jour échoue
      console.error('Erreur mise à jour membres retard:', error);
    }
  };

  // Générer les jours_details pour une affectation créée par rotation
  // Le premier jour est spécial : si heuresJour1 <= 4h, c'est une demi-journée PM
  const generateJoursDetailsRotation = (
    dateDebut: Date,
    dateFin: Date,
    heuresJour1: number,
    heuresJoursSuivants: number
  ): Array<{ date: string; period: 'AM' | 'PM'; value: number }> => {
    const result: Array<{ date: string; period: 'AM' | 'PM'; value: number }> = [];
    const allDays = eachDayOfInterval({ start: dateDebut, end: dateFin });

    allDays.forEach((day, index) => {
      // Exclure les weekends
      if (isWeekend(day)) return;

      const dateStr = format(day, 'yyyy-MM-dd');

      if (index === 0 && heuresJour1 <= 4) {
        // Premier jour en demi-journée : PM uniquement (rotation le matin)
        result.push({ date: dateStr, period: 'PM', value: 0.5 });
      } else {
        // Journée complète
        result.push({ date: dateStr, period: 'AM', value: 0.5 });
        result.push({ date: dateStr, period: 'PM', value: 0.5 });
      }
    });

    return result;
  };

  // Gérer la rotation d'équipe : terminer l'ancien chantier (date_fin = date début rotation) et affecter au nouveau
  const handleRotation = async () => {
    // Supporter les deux cas : conflit complet OU conflit partiel
    if (!chevauchementEquipe && !chevauchementEquipePartiel) return;
    setLoadingRotation(true);

    try {
      const dateDebutStr = format(dateDebutRotation, "yyyy-MM-dd");
      const dateFinStr = format(dateFinRotation, "yyyy-MM-dd");

      // Cas 1 : Conflit complet - mettre à jour les affectations de l'équipe sur l'ancien chantier
      if (chevauchementEquipe) {
        const { error: updateError } = await supabase
          .from("affectations_chantiers")
          .update({
            date_fin: dateDebutStr,
            heures_dernier_jour: heuresAncienChantier > 0 ? heuresAncienChantier : null,
            updated_at: new Date().toISOString(),
          })
          .eq("equipe_id", formData.equipe_id)
          .eq("chantier_id", chevauchementEquipe.chantierId);

        if (updateError) throw updateError;
      }

      // Cas 2 : Conflit partiel - mettre à jour les affectations des membres en conflit
      if (chevauchementEquipePartiel && !chevauchementEquipe && chevauchementEquipePartiel.membresEnConflit?.length > 0) {
        // Pour chaque membre en conflit, terminer son affectation actuelle
        for (const conflit of (chevauchementEquipePartiel.membresEnConflit || [])) {
          // Trouver l'affectation conflictuelle
          const affectationConflictuelle = affectationsDetails.find(aff =>
            aff.membre_equipe_id === conflit.membreId &&
            aff.chantier_id !== chantierId &&
            new Date(aff.date_debut) <= formData.date_fin &&
            new Date(aff.date_fin) >= formData.date_debut
          );

          if (affectationConflictuelle) {
            const { error: updateError } = await supabase
              .from("affectations_chantiers")
              .update({
                date_fin: dateDebutStr,
                heures_dernier_jour: heuresAncienChantier > 0 ? heuresAncienChantier : null,
                updated_at: new Date().toISOString(),
              })
              .eq("id", affectationConflictuelle.id);

            if (updateError) throw updateError;
          }
        }
      }

      // 2. Créer la nouvelle affectation sur le chantier actuel
      const membresEquipe = (membres || []).filter(m => m.equipe_id === formData.equipe_id);

      if (membresEquipe.length === 0) {
        throw new Error("Cette équipe n'a aucun membre.");
      }

      // Utiliser les valeurs du formulaire si l'utilisateur les a modifiées
      // Sinon, utiliser les heures configurées dans le modal de rotation
      const heuresParJourFinal = formData.heures_par_jour;

      // Pour jours_travailles : utiliser la valeur du formulaire si modifiée manuellement
      // ou si confirmée via le modal DayPeriodSelector
      const joursTravaillesFinal = (hasUserModifiedJours || hasConfirmedDaySelection)
        ? formData.jours_travailles
        : joursOuvresRotation;

      // Déterminer le nom du chantier source pour les notes
      const chantierSourceNom = chevauchementEquipe?.chantierNom ||
        (chevauchementEquipePartiel?.membresEnConflit?.[0] ?
          affectationsDetails.find(aff => aff.membre_equipe_id === chevauchementEquipePartiel.membresEnConflit?.[0]?.membreId && aff.chantier_id !== chantierId)?.chantier?.nom_chantier
          : "autre chantier");

      // Utiliser jours_details du formulaire si confirmé, sinon générer à partir de la rotation
      const joursDetailsRotation = (hasConfirmedDaySelection && selectedWorkDays.size > 0)
        ? convertWorkDaysToJsonb(selectedWorkDays)
        : generateJoursDetailsRotation(
            dateDebutRotation,
            dateFinRotation,
            heuresJour1Rotation,
            heuresJoursSuivantsRotation
          );

      const affectationsToInsert = membresEquipe.map(membre => ({
        chantier_id: chantierId,
        membre_equipe_id: membre.id,
        equipe_id: formData.equipe_id,
        date_debut: dateDebutStr,
        date_fin: dateFinStr,
        jours_travailles: joursTravaillesFinal,
        heures_par_jour: heuresParJourFinal,
        taux_horaire_specifique: membre.taux_horaire,
        charges_patronales_pct: membre.charges_patronales,
        notes: formData.notes || `Rotation depuis ${chantierSourceNom || "autre chantier"}`,
        jours_details: joursDetailsRotation,
      }));

      const { error: insertError } = await supabase
        .from("affectations_chantiers")
        .insert(affectationsToInsert);

      if (insertError) throw insertError;

      // Synchroniser les membres du retard : retirer les membres de la liste membres_mobilises_ids
      const ancienChantierId = chevauchementEquipe?.chantierId ||
        (chevauchementEquipePartiel?.membresEnConflit?.[0] ?
          affectationsDetails.find(aff => aff.membre_equipe_id === chevauchementEquipePartiel.membresEnConflit?.[0]?.membreId && aff.chantier_id !== chantierId)?.chantier_id
          : null);

      if (ancienChantierId) {
        for (const membre of membresEquipe) {
          await updateMembresRetardApresRotation(membre.id, ancienChantierId);
        }
      }

      setShowRotationModal(false);

      // Message de succès adapté au cas
      const messageDescription = chevauchementEquipe
        ? `L'équipe a terminé sur ${chevauchementEquipe.chantierNom} (${heuresAncienChantier}h aujourd'hui) et commence ici (${heuresJour1Rotation}h).`
        : `Les membres en conflit ont été libérés et toute l'équipe est maintenant affectée ici.`;

      toast({
        title: "Rotation effectuée",
        description: messageDescription,
      });

      // Rafraîchir les métriques des deux chantiers impactés
      await handleSuccessWithMetricsRefresh(ancienChantierId ? [ancienChantierId] : undefined);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingRotation(false);
    }
  };

  // Gérer le travail simultané : créer des affectations sur les deux chantiers avec heures réparties
  const handleTravailSimultane = async () => {
    if (!chevauchementEquipe) return;
    setLoading(true);

    try {
      const membresEquipe = (membres || []).filter(m => m.equipe_id === formData.equipe_id);

      if (membresEquipe.length === 0) {
        throw new Error("Cette équipe n'a aucun membre.");
      }

      const dateDebutStr = format(dateDebutSimultane, "yyyy-MM-dd");
      const dateFinStr = format(dateFinSimultane, "yyyy-MM-dd");

      // 1. Mettre à jour les affectations existantes sur l'autre chantier avec les nouvelles heures et dates
      const { error: updateError } = await supabase
        .from("affectations_chantiers")
        .update({
          heures_par_jour: heuresAutreChantier,
          date_debut: dateDebutStr,
          date_fin: dateFinStr,
          jours_travailles: joursOuvresSimultane,
        })
        .eq("equipe_id", formData.equipe_id)
        .eq("chantier_id", chevauchementEquipe.chantierId);

      if (updateError) throw updateError;

      // 2. Créer les nouvelles affectations sur le chantier actuel avec les heures réparties
      const affectationsToInsert = membresEquipe.map(membre => ({
        chantier_id: chantierId,
        membre_equipe_id: membre.id,
        equipe_id: formData.equipe_id,
        date_debut: dateDebutStr,
        date_fin: dateFinStr,
        jours_travailles: joursOuvresSimultane,
        heures_par_jour: heuresChantierActuel,
        taux_horaire_specifique: membre.taux_horaire,
        charges_patronales_pct: membre.charges_patronales,
        notes: formData.notes ? `${formData.notes} (travail simultané)` : "Travail simultané",
      }));

      const { error: insertError } = await supabase
        .from("affectations_chantiers")
        .insert(affectationsToInsert);

      if (insertError) throw insertError;

      toast({
        title: "Travail simultané configuré",
        description: `${joursOuvresSimultane} jours : ${heuresChantierActuel}h/jour ici + ${heuresAutreChantier}h/jour sur ${chevauchementEquipe.chantierNom}`,
      });

      setShowRepartitionModal(false);
      await handleSuccessWithMetricsRefresh([chevauchementEquipe.chantierId]);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Option A : Affecter uniquement les membres disponibles (ignore les membres en conflit)
  const handleAffecterMembresDisponibles = async () => {
    if (!chevauchementEquipePartiel || !formData.equipe_id) return;
    setLoading(true);

    try {
      const membresEquipe = (membres || []).filter(m => m.equipe_id === formData.equipe_id);
      const idsEnConflit = new Set((chevauchementEquipePartiel?.membresEnConflit || []).map(c => c.membreId));
      const membresDisponibles = membresEquipe.filter(m => !idsEnConflit.has(m.id));

      if (membresDisponibles.length === 0) {
        throw new Error("Aucun membre disponible dans cette équipe.");
      }

      const dateDebutStr = format(formData.date_debut, "yyyy-MM-dd");
      const dateFinStr = format(formData.date_fin, "yyyy-MM-dd");

      const affectationsToInsert = membresDisponibles.map(membre => ({
        chantier_id: chantierId,
        membre_equipe_id: membre.id,
        equipe_id: formData.equipe_id,
        date_debut: dateDebutStr,
        date_fin: dateFinStr,
        jours_travailles: formData.jours_travailles,
        heures_par_jour: formData.heures_par_jour,
        taux_horaire_specifique: membre.taux_horaire,
        charges_patronales_pct: membre.charges_patronales,
        notes: formData.notes || "Affectation partielle (membres disponibles uniquement)",
      }));

      const { error } = await supabase
        .from("affectations_chantiers")
        .insert(affectationsToInsert);

      if (error) throw error;

      toast({
        title: `${membresDisponibles.length} membre${membresDisponibles.length > 1 ? 's' : ''} affecté${membresDisponibles.length > 1 ? 's' : ''}`,
        description: `Les membres disponibles ont été affectés. ${chevauchementEquipePartiel?.membresEnConflit?.length || 0} membre(s) ignoré(s) car déjà affecté(s).`,
      });

      await handleSuccessWithMetricsRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Option C : Décaler l'affectation après la libération de tous les membres
  const handleDecalerAffectation = () => {
    if (!chevauchementEquipePartiel) return;

    // Calculer la nouvelle date de début (jour après la libération)
    const dateLiberation = new Date(chevauchementEquipePartiel.dateLiberation);
    const nouvelleDateDebut = new Date(dateLiberation.getTime() + 86400000); // +1 jour

    // Vérifier que la nouvelle date est avant la date de fin actuelle
    if (nouvelleDateDebut > formData.date_fin) {
      toast({
        title: "Impossible de décaler",
        description: "La date de libération est après la date de fin prévue du chantier.",
        variant: "destructive",
      });
      return;
    }

    // Recalculer les jours ouvrés
    const nouveauxJours = calculateJoursOuvres(nouvelleDateDebut, formData.date_fin);

    setFormData(prev => ({
      ...prev,
      date_debut: nouvelleDateDebut,
      jours_travailles: Math.max(1, nouveauxJours),
    }));

    toast({
      title: "Date de début modifiée",
      description: `Nouvelle date : ${format(nouvelleDateDebut, "dd/MM/yyyy", { locale: fr })}. Vous pouvez maintenant valider l'affectation.`,
    });
  };

  // Option D : Proactif - Affecter les disponibles maintenant + les autres après leur libération
  const handleAffectationProactive = async () => {
    if (!chevauchementEquipePartiel || !formData.equipe_id) return;
    setLoading(true);

    try {
      const membresEquipe = (membres || []).filter(m => m.equipe_id === formData.equipe_id);
      const conflitsMap = new Map(
        (chevauchementEquipePartiel?.membresEnConflit || []).map(c => [c.membreId, c])
      );

      const dateDebutStr = format(formData.date_debut, "yyyy-MM-dd");
      const dateFinStr = format(formData.date_fin, "yyyy-MM-dd");
      const dateFinChantier = formData.date_fin;

      const affectationsToInsert: any[] = [];

      for (const membre of membresEquipe) {
        const conflit = conflitsMap.get(membre.id);

        if (!conflit) {
          // Membre disponible : affectation immédiate normale
          affectationsToInsert.push({
            chantier_id: chantierId,
            membre_equipe_id: membre.id,
            equipe_id: formData.equipe_id,
            date_debut: dateDebutStr,
            date_fin: dateFinStr,
            jours_travailles: formData.jours_travailles,
            heures_par_jour: formData.heures_par_jour,
            taux_horaire_specifique: membre.taux_horaire,
            charges_patronales_pct: membre.charges_patronales,
            notes: formData.notes || null,
          });
        } else {
          // Membre en conflit : affectation différée après libération
          const dateLiberation = new Date(conflit.dateFin);
          const dateDebutDifferee = new Date(dateLiberation.getTime() + 86400000); // +1 jour

          // Vérifier que la date différée est avant la fin du chantier
          if (dateDebutDifferee <= dateFinChantier) {
            const joursOuvresDifferes = calculateJoursOuvres(dateDebutDifferee, dateFinChantier);

            if (joursOuvresDifferes > 0) {
              affectationsToInsert.push({
                chantier_id: chantierId,
                membre_equipe_id: membre.id,
                equipe_id: formData.equipe_id,
                date_debut: format(dateDebutDifferee, "yyyy-MM-dd"),
                date_fin: dateFinStr,
                jours_travailles: joursOuvresDifferes,
                heures_par_jour: formData.heures_par_jour,
                taux_horaire_specifique: membre.taux_horaire,
                charges_patronales_pct: membre.charges_patronales,
                notes: `Affectation différée (après ${conflit.chantierNom})`,
              });
            }
          }
        }
      }

      if (affectationsToInsert.length === 0) {
        throw new Error("Aucune affectation possible.");
      }

      const { error } = await supabase
        .from("affectations_chantiers")
        .insert(affectationsToInsert);

      if (error) throw error;

      const nbImmediat = membresEquipe.length - (chevauchementEquipePartiel?.membresEnConflit?.length || 0);
      const nbDiffere = affectationsToInsert.length - nbImmediat;

      toast({
        title: "Affectations créées",
        description: `${nbImmediat} membre(s) affecté(s) immédiatement, ${nbDiffere} affectation(s) différée(s).`,
      });

      await handleSuccessWithMetricsRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculer le total des heures pour validation
  const totalHeuresSimultane = heuresChantierActuel + heuresAutreChantier;
  const heuresValides = totalHeuresSimultane <= 16 && heuresChantierActuel > 0 && heuresAutreChantier > 0;

  // Validation heures simultanées pour membre
  const totalHeuresSimultaneMembre = heuresChantierActuelMembre + heuresAutreChantierMembre;
  const heuresValidesMembre = totalHeuresSimultaneMembre <= 16 && heuresChantierActuelMembre > 0 && heuresAutreChantierMembre > 0;

  // Ouvrir le modal de rotation pour membre individuel
  const openRotationModalMembre = () => {
    // Initialiser les heures en se basant sur l'affectation existante
    const heuresExistantes = chevauchementMembre?.heuresParJour || 8;
    setHeuresAncienChantierMembre(heuresExistantes / 2);
    setHeuresJour1RotationMembre(heuresExistantes / 2);
    setHeuresJoursSuivantsRotationMembre(heuresExistantes);
    // Utiliser la date de début du formulaire (pas aujourd'hui)
    setDateDebutRotationMembre(formData.date_debut);
    setDateFinRotationMembre(formData.date_fin);
    setShowRotationModalMembre(true);
  };

  // Gérer la rotation d'un membre individuel
  const handleRotationMembre = async () => {
    if (!chevauchementMembre) return;
    setLoadingRotation(true);

    try {
      const dateDebutStr = format(dateDebutRotationMembre, "yyyy-MM-dd");
      const dateFinStr = format(dateFinRotationMembre, "yyyy-MM-dd");

      // 1. Mettre à jour l'affectation du membre sur l'ancien chantier
      const { error: updateError } = await supabase
        .from("affectations_chantiers")
        .update({
          date_fin: dateDebutStr,
          heures_dernier_jour: heuresAncienChantierMembre > 0 ? heuresAncienChantierMembre : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", chevauchementMembre.affectationId);

      if (updateError) throw updateError;

      // 2. Créer la nouvelle affectation sur le chantier actuel
      const membre = membres.find(m => m.id === formData.membre_equipe_id);
      if (!membre) throw new Error("Membre non trouvé");

      // Utiliser les valeurs du formulaire si l'utilisateur les a modifiées
      const heuresParJourFinal = formData.heures_par_jour;

      // Pour jours_travailles : utiliser la valeur du formulaire si modifiée manuellement
      // ou si confirmée via le modal DayPeriodSelector
      const joursTravaillesFinal = (hasUserModifiedJours || hasConfirmedDaySelection)
        ? formData.jours_travailles
        : joursOuvresRotationMembre;

      // Utiliser jours_details du formulaire si confirmé, sinon générer à partir de la rotation
      const joursDetailsRotationMembre = (hasConfirmedDaySelection && selectedWorkDays.size > 0)
        ? convertWorkDaysToJsonb(selectedWorkDays)
        : generateJoursDetailsRotation(
            dateDebutRotationMembre,
            dateFinRotationMembre,
            heuresJour1RotationMembre,
            heuresJoursSuivantsRotationMembre
          );

      const { error: insertError } = await supabase
        .from("affectations_chantiers")
        .insert({
          chantier_id: chantierId,
          membre_equipe_id: formData.membre_equipe_id,
          date_debut: dateDebutStr,
          date_fin: dateFinStr,
          jours_travailles: joursTravaillesFinal,
          heures_par_jour: heuresParJourFinal,
          taux_horaire_specifique: membre.taux_horaire,
          charges_patronales_pct: membre.charges_patronales,
          notes: formData.notes || `Rotation depuis ${chevauchementMembre.chantierNom}`,
          jours_details: joursDetailsRotationMembre,
        });

      if (insertError) throw insertError;

      // Synchroniser les membres du retard : retirer le membre de la liste membres_mobilises_ids
      await updateMembresRetardApresRotation(formData.membre_equipe_id, chevauchementMembre.chantierId);

      setShowRotationModalMembre(false);
      toast({
        title: "Rotation effectuée",
        description: `${membre.prenom} ${membre.nom} a terminé sur ${chevauchementMembre.chantierNom} (${heuresAncienChantierMembre}h) et commence ici (${heuresJour1RotationMembre}h).`,
      });

      // Rafraîchir les métriques des deux chantiers impactés
      await handleSuccessWithMetricsRefresh([chevauchementMembre.chantierId]);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingRotation(false);
    }
  };

  // Gérer le travail simultané pour un membre individuel
  const handleTravailSimultaneMembre = async () => {
    if (!chevauchementMembre) return;
    setLoading(true);

    try {
      const membre = membres.find(m => m.id === formData.membre_equipe_id);
      if (!membre) throw new Error("Membre non trouvé");

      const dateDebutStr = format(dateDebutSimultaneMembre, "yyyy-MM-dd");
      const dateFinStr = format(dateFinSimultaneMembre, "yyyy-MM-dd");

      // 1. Mettre à jour l'affectation existante sur l'autre chantier avec les nouvelles heures et dates
      const { error: updateError } = await supabase
        .from("affectations_chantiers")
        .update({
          heures_par_jour: heuresAutreChantierMembre,
          date_debut: dateDebutStr,
          date_fin: dateFinStr,
          jours_travailles: joursOuvresSimultaneMembre,
        })
        .eq("id", chevauchementMembre.affectationId);

      if (updateError) throw updateError;

      // 2. Créer la nouvelle affectation sur le chantier actuel avec les heures réparties
      const { error: insertError } = await supabase
        .from("affectations_chantiers")
        .insert({
          chantier_id: chantierId,
          membre_equipe_id: formData.membre_equipe_id,
          date_debut: dateDebutStr,
          date_fin: dateFinStr,
          jours_travailles: joursOuvresSimultaneMembre,
          heures_par_jour: heuresChantierActuelMembre,
          taux_horaire_specifique: membre.taux_horaire,
          charges_patronales_pct: membre.charges_patronales,
          notes: formData.notes ? `${formData.notes} (travail simultané)` : "Travail simultané",
        });

      if (insertError) throw insertError;

      toast({
        title: "Travail simultané configuré",
        description: `${joursOuvresSimultaneMembre} jours : ${heuresChantierActuelMembre}h/jour ici + ${heuresAutreChantierMembre}h/jour sur ${chevauchementMembre.chantierNom}`,
      });

      setShowRepartitionModalMembre(false);
      await handleSuccessWithMetricsRefresh([chevauchementMembre.chantierId]);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedMembre = membres.find(m => m.id === formData.membre_equipe_id);

  // Blocage si chantier suspendu
  if (chantier?.etat_chantier === 'suspendu') {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <div className="p-2 rounded-full bg-amber-500/20">
            <Pause className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-600 dark:text-amber-400">Chantier suspendu</h3>
            <p className="text-sm text-muted-foreground">
              Ce chantier est actuellement suspendu. Vous ne pouvez pas créer de nouvelles affectations.
            </p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Pour ajouter des affectations, vous devez d'abord reprendre le chantier depuis le menu d'actions.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Type d'affectation</Label>
        <RadioGroup value={selectedType} onValueChange={(value: any) => setSelectedType(value)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="membre" id="type-membre" />
            <Label htmlFor="type-membre">Membre individuel</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="equipe" id="type-equipe" />
            <Label htmlFor="type-equipe">Équipe complète</Label>
          </div>
        </RadioGroup>
        <p className="text-xs text-muted-foreground mt-1">Membre : affecte une personne. Équipe : affecte tout le groupe avec le même planning.</p>
      </div>

      {selectedType === "membre" ? (
        <div className="space-y-2">
          <Label htmlFor="membre">Membre *</Label>
          <Select
            value={formData.membre_equipe_id}
            onValueChange={(value) => {
              setFormData({ ...formData, membre_equipe_id: value });
              setValidationError(null);
            }}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner un membre" />
            </SelectTrigger>
            <SelectContent>
              {/* Filtrer uniquement sur_chantier, en_rotation et disponible */}
              {(() => {
                const membresFiltres = (membres || []).filter(m =>
                  m.statut === 'sur_chantier' || m.statut === 'en_rotation' || m.statut === 'disponible'
                );

                const surChantier = membresFiltres.filter(m => m.statut === 'sur_chantier');
                const enRotation = membresFiltres.filter(m => m.statut === 'en_rotation');
                const disponibles = membresFiltres.filter(m => m.statut === 'disponible');

                const renderMembre = (membre: any) => {
                  const chantierAffecte = affectationsMembres[membre.id];
                  return (
                    <SelectItem key={membre.id} value={membre.id}>
                      <div className="flex items-center gap-2 w-full">
                        <span>{membre.prenom} {membre.nom} - {membre.poste} ({membre.taux_horaire}€/h)</span>
                        {chantierAffecte ? (
                          <span className="ml-auto text-xs font-bold text-amber-500">
                            sur {chantierAffecte.chantiers.join(" / ")} jusqu'au {format(new Date(chantierAffecte.dateFin), "dd/MM", { locale: fr })}
                          </span>
                        ) : (
                          <span className="ml-auto text-xs font-medium text-green-500">
                            Disponible
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                };

                const toggleSection = (section: string, e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
                };

                return (
                  <>
                    {surChantier.length > 0 && (
                      <SelectGroup>
                        <div
                          className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent rounded-sm"
                          onClick={(e) => toggleSection('sur_chantier', e)}
                        >
                          {expandedSections.sur_chantier ? (
                            <ChevronDown className="h-4 w-4 text-amber-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-amber-500" />
                          )}
                          <span className="text-amber-500 font-semibold text-sm">Sur chantier ({surChantier.length})</span>
                        </div>
                        {expandedSections.sur_chantier && surChantier.map(renderMembre)}
                      </SelectGroup>
                    )}
                    {enRotation.length > 0 && (
                      <SelectGroup>
                        <div
                          className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent rounded-sm"
                          onClick={(e) => toggleSection('en_rotation', e)}
                        >
                          {expandedSections.en_rotation ? (
                            <ChevronDown className="h-4 w-4 text-blue-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-blue-500" />
                          )}
                          <span className="text-blue-500 font-semibold text-sm">En rotation ({enRotation.length})</span>
                        </div>
                        {expandedSections.en_rotation && enRotation.map(renderMembre)}
                      </SelectGroup>
                    )}
                    {disponibles.length > 0 && (
                      <SelectGroup>
                        <div
                          className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent rounded-sm"
                          onClick={(e) => toggleSection('disponible', e)}
                        >
                          {expandedSections.disponible ? (
                            <ChevronDown className="h-4 w-4 text-green-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-green-500 font-semibold text-sm">Disponibles ({disponibles.length})</span>
                        </div>
                        {expandedSections.disponible && disponibles.map(renderMembre)}
                      </SelectGroup>
                    )}
                  </>
                );
              })()}
            </SelectContent>
          </Select>

          {/* Erreur de validation - Aucun membre sélectionné */}
          {validationError && selectedType === "membre" && (
            <InlineError
              message={validationError}
              className="mt-2"
              onDismiss={() => setValidationError(null)}
            />
          )}

          {/* Alerte de chevauchement de membre */}
          {chevauchementMembre && (
            <div className="mt-2 p-4 rounded-lg border border-amber-500 bg-amber-500/10">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-500">
                    Chevauchement détecté
                  </p>
                  <p className="text-sm text-amber-400 mt-1">
                    Ce membre est déjà affecté au chantier <strong>{chevauchementMembre.chantierNom}</strong> du{" "}
                    {format(new Date(chevauchementMembre.dateDebut), "dd/MM/yyyy", { locale: fr })} au{" "}
                    {format(new Date(chevauchementMembre.dateFin), "dd/MM/yyyy", { locale: fr })}.
                  </p>
                  <p className="text-sm text-muted-foreground mt-3 mb-3">
                    Que souhaitez-vous faire ?
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-blue-500 text-blue-500 hover:bg-blue-500/10"
                      onClick={openRotationModalMembre}
                      disabled={loadingRotation}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {loadingRotation ? "Rotation en cours..." : `Rotation : retirer de ${chevauchementMembre.chantierNom} et affecter ici`}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-green-500 text-green-500 hover:bg-green-500/10"
                      onClick={() => setShowRepartitionModalMembre(true)}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Travail simultané : répartir les heures entre les 2 chantiers
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="equipe">Équipe *</Label>
          <Select
            value={formData.equipe_id}
            onValueChange={(value) => {
              setFormData({ ...formData, equipe_id: value });
              setValidationError(null);
            }}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner une équipe" />
            </SelectTrigger>
            <SelectContent>
              {(equipes || []).map((equipe) => {
                const membresEquipe = (membres || []).filter(m => m.equipe_id === equipe.id);
                const dispo = disponibiliteParEquipe[equipe.id];
                const nbDispo = dispo?.disponibles ?? membresEquipe.length;
                const nbTotal = dispo?.total ?? membresEquipe.length;
                const hasConflitPartiel = dispo && (dispo.membresEnConflit?.length || 0) > 0 && dispo.disponibles > 0;
                const tousEnConflit = dispo && dispo.disponibles === 0 && dispo.total > 0;

                return (
                  <SelectItem key={equipe.id} value={equipe.id} className="p-0">
                    <HoverCard openDelay={200} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <div className="flex items-center gap-2 w-full py-2 px-2 cursor-pointer">
                          {equipe.pictogramme && equipe.couleur && (
                            <EquipeIcon
                              pictogramme={equipe.pictogramme}
                              couleur={equipe.couleur}
                              size="sm"
                              showBackground={true}
                            />
                          )}
                          <span>{equipe.nom} {equipe.specialite && `- ${equipe.specialite}`}</span>
                          {/* Affichage de la disponibilité */}
                          {nbTotal === 0 ? (
                            <span className="ml-auto text-xs font-medium text-muted-foreground">
                              Aucun membre
                            </span>
                          ) : tousEnConflit ? (
                            <span className="ml-auto text-xs font-bold text-red-500">
                              0/{nbTotal} dispo
                            </span>
                          ) : hasConflitPartiel ? (
                            <span className="ml-auto text-xs font-bold text-orange-500">
                              {nbDispo}/{nbTotal} dispo
                            </span>
                          ) : (
                            <span className="ml-auto text-xs font-medium text-green-500">
                              {nbTotal}/{nbTotal} dispo
                            </span>
                          )}
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent side="right" align="start" sideOffset={10} avoidCollisions={false} className="w-72 p-3 z-[9999]">
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Membres ({membresEquipe.length})
                          </p>
                          {membresEquipe.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">Aucun membre dans cette équipe</p>
                          ) : (
                            <div className="space-y-1.5">
                              {membresEquipe.map((membre) => {
                                const conflitMembre = dispo?.membresEnConflit?.find(c => c.membreId === membre.id);
                                return (
                                  <div key={membre.id} className="flex items-center gap-2 text-sm">
                                    <User className={cn(
                                      "h-3.5 w-3.5",
                                      conflitMembre ? "text-orange-500" : "text-green-500"
                                    )} />
                                    <span className={conflitMembre ? "text-orange-500" : ""}>
                                      {membre.prenom} {membre.nom}
                                    </span>
                                    {conflitMembre ? (
                                      <span className="text-xs text-orange-400 ml-auto">
                                        → {conflitMembre.chantierNom} ({format(new Date(conflitMembre.dateFin), "dd/MM", { locale: fr })})
                                      </span>
                                    ) : membre.poste ? (
                                      <span className="text-xs text-muted-foreground">({membre.poste})</span>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Erreur de validation - Aucune équipe sélectionnée */}
          {validationError && selectedType === "equipe" && (
            <InlineError
              message={validationError}
              className="mt-2"
              onDismiss={() => setValidationError(null)}
            />
          )}

          {/* Alerte de chevauchement PARTIEL d'équipe (certains membres occupés) */}
          {chevauchementEquipePartiel && !chevauchementEquipe && (
            <div className="mt-2 p-4 rounded-lg border border-orange-500 bg-orange-500/10">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-orange-500">
                    Conflit partiel : {chevauchementEquipePartiel.disponibles}/{chevauchementEquipePartiel.total} membres disponibles
                  </p>
                  <div className="text-sm text-orange-400 mt-1">
                    <p className="mb-1">Membres en conflit :</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {(chevauchementEquipePartiel.membresEnConflit || []).map(conflit => (
                        <li key={conflit.membreId}>
                          <strong>{conflit.membreNom}</strong> sur {conflit.chantierNom} jusqu'au {format(new Date(conflit.dateFin), "dd/MM", { locale: fr })}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 mb-3">
                    Que souhaitez-vous faire ?
                  </p>
                  <div className="flex flex-col gap-2">
                    {/* Option A : Équipe partielle (ignorer les membres en conflit) */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-green-500 text-green-500 hover:bg-green-500/10"
                      onClick={handleAffecterMembresDisponibles}
                      disabled={loading}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Équipe partielle : {chevauchementEquipePartiel.disponibles} membres ({(chevauchementEquipePartiel.membresEnConflit || []).map(c => c.membreNom.split(' ')[0]).join(', ')} ignoré{(chevauchementEquipePartiel.membresEnConflit?.length || 0) > 1 ? 's' : ''})
                    </Button>

                    {/* Option B : Rotation (libérer les membres en conflit) */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-blue-500 text-blue-500 hover:bg-blue-500/10"
                      onClick={openRotationModal}
                      disabled={loadingRotation}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retirer {(chevauchementEquipePartiel.membresEnConflit || []).map(c => c.membreNom.split(' ')[0]).join(', ')} du chantier actuel → {chevauchementEquipePartiel.total} membres ici
                    </Button>

                    {/* Option C : Reporter l'affectation */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-purple-500 text-purple-500 hover:bg-purple-500/10"
                      onClick={handleDecalerAffectation}
                      disabled={loading}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Reporter au {format(new Date(new Date(chevauchementEquipePartiel.dateLiberation).getTime() + 86400000), "dd/MM", { locale: fr })} (équipe complète dispo)
                    </Button>

                    {/* Option D : Affectation progressive */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-amber-500 text-amber-500 hover:bg-amber-500/10"
                      onClick={handleAffectationProactive}
                      disabled={loading}
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      {chevauchementEquipePartiel.disponibles} maintenant + {(chevauchementEquipePartiel.membresEnConflit || []).map(c => c.membreNom.split(' ')[0]).join(', ')} rejoint le {format(new Date(new Date(chevauchementEquipePartiel.dateLiberation).getTime() + 86400000), "dd/MM", { locale: fr })}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Alerte de chevauchement COMPLET d'équipe (toute l'équipe occupée) */}
          {chevauchementEquipe && (
            <div className="mt-2 p-4 rounded-lg border border-amber-500 bg-amber-500/10">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-500">
                    Chevauchement complet
                  </p>
                  <p className="text-sm text-amber-400 mt-1">
                    Cette équipe est déjà affectée au chantier <strong>{chevauchementEquipe.chantierNom}</strong> du{" "}
                    {format(new Date(chevauchementEquipe.dateDebut), "dd/MM/yyyy", { locale: fr })} au{" "}
                    {format(new Date(chevauchementEquipe.dateFin), "dd/MM/yyyy", { locale: fr })}.
                  </p>
                  <p className="text-sm text-muted-foreground mt-3 mb-3">
                    Que souhaitez-vous faire ?
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-blue-500 text-blue-500 hover:bg-blue-500/10"
                      onClick={openRotationModal}
                      disabled={loadingRotation}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {loadingRotation ? "Rotation en cours..." : `Rotation : retirer de ${chevauchementEquipe.chantierNom} et affecter ici`}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start border-green-500 text-green-500 hover:bg-green-500/10"
                      onClick={() => setShowRepartitionModal(true)}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Travail simultané : répartir les heures entre les 2 chantiers
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date début *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.date_debut && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date_debut ? format(formData.date_debut, "PPP", { locale: fr }) : "Choisir"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.date_debut}
                onSelect={(date) => date && setFormData({ ...formData, date_debut: date })}
                locale={fr}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Date fin *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.date_fin && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date_fin ? format(formData.date_fin, "PPP", { locale: fr }) : "Choisir"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.date_fin}
                onSelect={(date) => handleDateFinChange(date)}
                locale={fr}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="jours">Jours travaillés *</Label>
          <div className="flex gap-2">
            <Input
              id="jours"
              type="number"
              step="0.5"
              min="0.5"
              value={formData.jours_travailles}
              onChange={(e) => {
                const newValue = parseFloat(e.target.value);
                // Marquer que l'utilisateur a modifié manuellement
                setHasUserModifiedJours(true);
                setFormData({ ...formData, jours_travailles: newValue });

                // Calculer l'écart avec les jours ouvrés
                if (!isNaN(newValue) && formData.date_debut && formData.date_fin) {
                  const joursOuvres = calculateJoursOuvres(formData.date_debut, formData.date_fin, inclureWeekendFin);
                  const ecart = newValue - joursOuvres;

                  // Ouvrir le modal si écart non nul (positif ou négatif)
                  if (ecart !== 0) {
                    setHasConfirmedDaySelection(false);
                    // Pré-remplir la sélection automatiquement
                    const autoSelection = generateAutoSelection(formData.date_debut, formData.date_fin, newValue);
                    setSelectedWorkDays(autoSelection);
                    setPendingDayPeriodModal(true);
                  }
                }
              }}
              required
            />
            <Button type="button" variant="outline" size="icon" onClick={autoCalculateJours} title="Calculer automatiquement">
              <Calculator className="h-4 w-4" />
            </Button>
            {/* Bouton pour modifier la sélection des jours si déjà confirmé */}
            {hasConfirmedDaySelection && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowDayPeriodModal(true)}
                title="Modifier les jours/demi-journées"
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
          {/* Indicateur de sélection confirmée */}
          {hasConfirmedDaySelection && selectedWorkDays.size > 0 && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <span>✓</span>
              {calculateTotalSelectedDays(selectedWorkDays)} jour(s) sélectionné(s)
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">Calculé automatiquement (jours travaillés, hors weekends). Modifiable pour les demi-journées (ex: 3.5).</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="heures">Heures par jour *</Label>
          <Input
            id="heures"
            type="number"
            step="0.5"
            min="0.5"
            max="24"
            value={formData.heures_par_jour}
            onChange={(e) => setFormData({ ...formData, heures_par_jour: parseFloat(e.target.value) })}
            required
          />
        </div>
      </div>

      {selectedType === "membre" && selectedMembre && (
        <div className="space-y-2">
          <Label>Taux horaire</Label>
          <RadioGroup
            value={formData.useTauxSpecifique ? "specifique" : "normal"}
            onValueChange={(value) => setFormData({ ...formData, useTauxSpecifique: value === "specifique" })}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="normal" id="taux-normal" />
              <Label htmlFor="taux-normal">Utiliser taux du membre ({selectedMembre.taux_horaire}€/h)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="specifique" id="taux-specifique" />
              <Label htmlFor="taux-specifique">Taux spécifique:</Label>
              {formData.useTauxSpecifique && (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="€/h"
                  value={formData.taux_horaire_specifique}
                  onChange={(e) => setFormData({ ...formData, taux_horaire_specifique: e.target.value })}
                  className="w-32"
                />
              )}
            </div>
          </RadioGroup>
          <p className="text-xs text-muted-foreground mt-1">Laissez vide pour utiliser le taux par défaut du membre. Utile pour les primes ou tarifs chantier.</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Ex: Semaine de finition, Travaux gros-œuvre..."
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
        />
      </div>

      <div className="p-4 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <span className="font-semibold">COÛT CALCULÉ:</span>
          <span className="text-2xl font-bold text-primary">
            {calculatedCost.toFixed(2)} €
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={loading || !!chevauchementEquipe || !!chevauchementMembre}>
          {loading ? "Enregistrement..." : affectation ? "Modifier" : "Ajouter"}
        </Button>
      </div>

      {/* Modal de répartition des heures pour travail simultané */}
      <Dialog open={showRepartitionModal} onOpenChange={setShowRepartitionModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Travail simultané - Équipe
            </DialogTitle>
            <DialogDescription>
              Définissez la période et répartissez les heures entre les deux chantiers.
            </DialogDescription>
          </DialogHeader>

          {chevauchementEquipe && (
            <div className="space-y-4">
              {/* Dates de l'affectation simultanée */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateDebutSimultane && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateDebutSimultane ? format(dateDebutSimultane, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateDebutSimultane}
                        onSelect={(date) => date && setDateDebutSimultane(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateFinSimultane && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFinSimultane ? format(dateFinSimultane, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateFinSimultane}
                        onSelect={(date) => date && setDateFinSimultane(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Info jours ouvrés */}
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  <strong>{joursOuvresSimultane}</strong> jour{joursOuvresSimultane > 1 ? 's' : ''} travaillé{joursOuvresSimultane > 1 ? 's' : ''}
                </span>
              </div>

              {/* Chantier actuel */}
              <div className="p-3 rounded-lg border bg-primary/5">
                <Label className="text-sm font-semibold text-primary">
                  {chantier?.reference_chantier || chantier?.nom_chantier || "Ce chantier"}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min="0.5"
                    max="15.5"
                    step="0.5"
                    value={heuresChantierActuel}
                    onChange={(e) => setHeuresChantierActuel(parseFloat(e.target.value) || 0)}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">heures/jour</span>
                </div>
              </div>

              {/* Autre chantier */}
              <div className="p-3 rounded-lg border bg-amber-500/5">
                <Label className="text-sm font-semibold text-amber-500">
                  {chevauchementEquipe.chantierNom}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min="0.5"
                    max="15.5"
                    step="0.5"
                    value={heuresAutreChantier}
                    onChange={(e) => setHeuresAutreChantier(parseFloat(e.target.value) || 0)}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">heures/jour</span>
                </div>
              </div>

              {/* Total */}
              <div className={cn(
                "p-3 rounded-lg border text-center",
                heuresValides && joursOuvresSimultane > 0 ? "bg-green-500/10 border-green-500" : "bg-red-500/10 border-red-500"
              )}>
                <p className="text-sm font-semibold">
                  Total : {totalHeuresSimultane}h/jour
                </p>
                {!heuresValides && (
                  <p className="text-xs text-red-500 mt-1">
                    {totalHeuresSimultane > 16
                      ? "Maximum 16h/jour autorisé"
                      : "Les heures doivent être supérieures à 0"}
                  </p>
                )}
                {joursOuvresSimultane <= 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Sélectionnez des dates valides
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepartitionModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleTravailSimultane}
              disabled={loading || !heuresValides || joursOuvresSimultane <= 0}
            >
              {loading ? "Enregistrement..." : "Confirmer la répartition"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de répartition des heures pour travail simultané - MEMBRE */}
      <Dialog open={showRepartitionModalMembre} onOpenChange={setShowRepartitionModalMembre}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Travail simultané - Membre
            </DialogTitle>
            <DialogDescription>
              Définissez la période et répartissez les heures entre les deux chantiers.
            </DialogDescription>
          </DialogHeader>

          {chevauchementMembre && (
            <div className="space-y-4">
              {/* Dates de l'affectation simultanée */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateDebutSimultaneMembre && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateDebutSimultaneMembre ? format(dateDebutSimultaneMembre, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateDebutSimultaneMembre}
                        onSelect={(date) => date && setDateDebutSimultaneMembre(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateFinSimultaneMembre && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFinSimultaneMembre ? format(dateFinSimultaneMembre, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateFinSimultaneMembre}
                        onSelect={(date) => date && setDateFinSimultaneMembre(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Info jours ouvrés */}
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  <strong>{joursOuvresSimultaneMembre}</strong> jour{joursOuvresSimultaneMembre > 1 ? 's' : ''} travaillé{joursOuvresSimultaneMembre > 1 ? 's' : ''}
                </span>
              </div>

              {/* Chantier actuel */}
              <div className="p-3 rounded-lg border bg-primary/5">
                <Label className="text-sm font-semibold text-primary">
                  {chantier?.reference_chantier || chantier?.nom_chantier || "Ce chantier"}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min="0.5"
                    max="15.5"
                    step="0.5"
                    value={heuresChantierActuelMembre}
                    onChange={(e) => setHeuresChantierActuelMembre(parseFloat(e.target.value) || 0)}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">heures/jour</span>
                </div>
              </div>

              {/* Autre chantier */}
              <div className="p-3 rounded-lg border bg-amber-500/5">
                <Label className="text-sm font-semibold text-amber-500">
                  {chevauchementMembre.chantierNom}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min="0.5"
                    max="15.5"
                    step="0.5"
                    value={heuresAutreChantierMembre}
                    onChange={(e) => setHeuresAutreChantierMembre(parseFloat(e.target.value) || 0)}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">heures/jour</span>
                </div>
              </div>

              {/* Total */}
              <div className={cn(
                "p-3 rounded-lg border text-center",
                heuresValidesMembre && joursOuvresSimultaneMembre > 0 ? "bg-green-500/10 border-green-500" : "bg-red-500/10 border-red-500"
              )}>
                <p className="text-sm font-semibold">
                  Total : {totalHeuresSimultaneMembre}h/jour
                </p>
                {!heuresValidesMembre && (
                  <p className="text-xs text-red-500 mt-1">
                    {totalHeuresSimultaneMembre > 16
                      ? "Maximum 16h/jour autorisé"
                      : "Les heures doivent être supérieures à 0"}
                  </p>
                )}
                {joursOuvresSimultaneMembre <= 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Sélectionnez des dates valides
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepartitionModalMembre(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleTravailSimultaneMembre}
              disabled={loading || !heuresValidesMembre || joursOuvresSimultaneMembre <= 0}
            >
              {loading ? "Enregistrement..." : "Confirmer la répartition"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de rotation avec distinction heures jour 1 / jours suivants - MEMBRE */}
      <Dialog open={showRotationModalMembre} onOpenChange={setShowRotationModalMembre}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-500">
              <RefreshCw className="h-5 w-5" />
              Rotation de membre
            </DialogTitle>
            <DialogDescription>
              Définissez les dates et heures de travail sur le nouveau chantier.
            </DialogDescription>
          </DialogHeader>

          {chevauchementMembre && (
            <div className="space-y-4">
              {/* Visualisation rotation */}
              <div className="flex items-center gap-3">
                <div className="flex-1 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                  <p className="text-xs text-red-400 mb-1">Quitte</p>
                  <p className="font-medium text-sm truncate">{chevauchementMembre.chantierNom}</p>
                </div>
                <RefreshCw className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                  <p className="text-xs text-green-400 mb-1">Rejoint</p>
                  <p className="font-medium text-sm truncate">
                    {chantier?.reference_chantier || chantier?.nom_chantier || "Ce chantier"}
                  </p>
                </div>
              </div>

              {/* Dates de la nouvelle affectation */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateDebutRotationMembre && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateDebutRotationMembre ? format(dateDebutRotationMembre, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateDebutRotationMembre}
                        onSelect={(date) => date && setDateDebutRotationMembre(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateFinRotationMembre && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFinRotationMembre ? format(dateFinRotationMembre, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateFinRotationMembre}
                        onSelect={(date) => date && setDateFinRotationMembre(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Info jours ouvrés */}
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  <strong>{joursOuvresRotationMembre}</strong> jour{joursOuvresRotationMembre > 1 ? 's' : ''} travaillé{joursOuvresRotationMembre > 1 ? 's' : ''}
                </span>
              </div>

              {/* Heures sur l'ancien chantier le jour de rotation */}
              <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5">
                <Label htmlFor="heuresAncienChantierMembre" className="flex items-center gap-2 text-red-500 text-sm font-semibold">
                  <Clock className="w-3.5 h-3.5" />
                  Heures sur l'ancien chantier ({format(dateDebutRotationMembre, "dd/MM", { locale: fr })})
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Temps travaillé sur {chevauchementMembre.chantierNom} avant la rotation
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="heuresAncienChantierMembre"
                    type="number"
                    value={heuresAncienChantierMembre}
                    onChange={(e) => setHeuresAncienChantierMembre(parseFloat(e.target.value) || 0)}
                    min={0}
                    max={12}
                    step={0.5}
                    className="w-20 h-9"
                  />
                  <span className="text-sm text-muted-foreground">heures</span>
                </div>
              </div>

              {/* Heures par jour - avec distinction jour 1 / jours suivants si plusieurs jours */}
              {joursOuvresRotationMembre === 1 ? (
                // Un seul jour
                <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/5">
                  <Label htmlFor="heuresJour1RotMembre" className="flex items-center gap-2 text-green-500 text-sm font-semibold">
                    <Clock className="w-3.5 h-3.5" />
                    Aujourd'hui sur {chantier?.reference_chantier || chantier?.nom_chantier || "ce chantier"}
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      id="heuresJour1RotMembre"
                      type="number"
                      value={heuresJour1RotationMembre}
                      onChange={(e) => setHeuresJour1RotationMembre(parseFloat(e.target.value) || 4)}
                      min={0.5}
                      max={12}
                      step={0.5}
                      className="w-20 h-9"
                    />
                    <span className="text-sm text-muted-foreground">heures</span>
                  </div>
                </div>
              ) : joursOuvresRotationMembre > 1 ? (
                // Plusieurs jours : distinction jour 1 / jours suivants
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                    <Label htmlFor="heuresJour1RotMembre" className="flex items-center gap-2 text-amber-500 text-sm font-semibold">
                      <Clock className="w-3.5 h-3.5" />
                      Aujourd'hui (jour de rotation) sur {chantier?.reference_chantier || chantier?.nom_chantier || "ce chantier"}
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        id="heuresJour1RotMembre"
                        type="number"
                        value={heuresJour1RotationMembre}
                        onChange={(e) => setHeuresJour1RotationMembre(parseFloat(e.target.value) || 4)}
                        min={0.5}
                        max={12}
                        step={0.5}
                        className="w-20 h-9"
                      />
                      <span className="text-sm text-muted-foreground">heures</span>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
                    <Label htmlFor="heuresSuivantsRotMembre" className="flex items-center gap-2 text-primary text-sm font-semibold">
                      <Clock className="w-3.5 h-3.5" />
                      Jours suivants ({joursOuvresRotationMembre - 1} jour{joursOuvresRotationMembre > 2 ? 's' : ''}) sur {chantier?.reference_chantier || chantier?.nom_chantier || "ce chantier"}
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        id="heuresSuivantsRotMembre"
                        type="number"
                        value={heuresJoursSuivantsRotationMembre}
                        onChange={(e) => setHeuresJoursSuivantsRotationMembre(parseFloat(e.target.value) || 8)}
                        min={0.5}
                        max={12}
                        step={0.5}
                        className="w-20 h-9"
                      />
                      <span className="text-sm text-muted-foreground">heures/jour</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Récapitulatif */}
              {joursOuvresRotationMembre > 0 && (
                <div className="flex items-start gap-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg text-xs">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <div className="flex flex-col gap-0.5">
                    <span>Ancien chantier : <strong>{heuresAncienChantierMembre}h</strong> le {format(dateDebutRotationMembre, "dd/MM", { locale: fr })}</span>
                    {joursOuvresRotationMembre === 1 ? (
                      <span>Nouveau chantier : <strong>{heuresJour1RotationMembre}h</strong> le {format(dateDebutRotationMembre, "dd/MM", { locale: fr })}</span>
                    ) : (
                      <>
                        <span>Nouveau chantier jour 1 : <strong>{heuresJour1RotationMembre}h</strong></span>
                        <span>Puis : <strong>{heuresJoursSuivantsRotationMembre}h/jour</strong> pendant {joursOuvresRotationMembre - 1} jour{joursOuvresRotationMembre > 2 ? 's' : ''}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRotationModalMembre(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleRotationMembre}
              disabled={loadingRotation || joursOuvresRotationMembre <= 0}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {loadingRotation ? "Rotation en cours..." : "Confirmer la rotation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de rotation avec distinction heures jour 1 / jours suivants */}
      <Dialog open={showRotationModal} onOpenChange={setShowRotationModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-500">
              <RefreshCw className="h-5 w-5" />
              Rotation d'équipe
            </DialogTitle>
            <DialogDescription>
              Définissez les dates et heures de travail sur le nouveau chantier.
            </DialogDescription>
          </DialogHeader>

          {chevauchementEquipe && (
            <div className="space-y-4">
              {/* Visualisation rotation */}
              <div className="flex items-center gap-3">
                <div className="flex-1 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                  <p className="text-xs text-red-400 mb-1">Quitte</p>
                  <p className="font-medium text-sm truncate">{chevauchementEquipe.chantierNom}</p>
                </div>
                <RefreshCw className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                  <p className="text-xs text-green-400 mb-1">Rejoint</p>
                  <p className="font-medium text-sm truncate">
                    {chantier?.reference_chantier || chantier?.nom_chantier || "Ce chantier"}
                  </p>
                </div>
              </div>

              {/* Dates de la nouvelle affectation */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateDebutRotation && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateDebutRotation ? format(dateDebutRotation, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateDebutRotation}
                        onSelect={(date) => date && setDateDebutRotation(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-9", !dateFinRotation && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFinRotation ? format(dateFinRotation, "dd/MM/yyyy", { locale: fr }) : "Choisir"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateFinRotation}
                        onSelect={(date) => date && setDateFinRotation(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Info jours ouvrés */}
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  <strong>{joursOuvresRotation}</strong> jour{joursOuvresRotation > 1 ? 's' : ''} travaillé{joursOuvresRotation > 1 ? 's' : ''}
                </span>
              </div>

              {/* Heures sur l'ancien chantier le jour de rotation */}
              <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5">
                <Label htmlFor="heuresAncienChantier" className="flex items-center gap-2 text-red-500 text-sm font-semibold">
                  <Clock className="w-3.5 h-3.5" />
                  Heures sur l'ancien chantier ({format(dateDebutRotation, "dd/MM", { locale: fr })})
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Temps travaillé sur {chevauchementEquipe.chantierNom} avant la rotation
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="heuresAncienChantier"
                    type="number"
                    value={heuresAncienChantier}
                    onChange={(e) => setHeuresAncienChantier(parseFloat(e.target.value) || 0)}
                    min={0}
                    max={12}
                    step={0.5}
                    className="w-20 h-9"
                  />
                  <span className="text-sm text-muted-foreground">heures</span>
                </div>
              </div>

              {/* Heures par jour - avec distinction jour 1 / jours suivants si plusieurs jours */}
              {joursOuvresRotation === 1 ? (
                // Un seul jour
                <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/5">
                  <Label htmlFor="heuresJour1Rot" className="flex items-center gap-2 text-green-500 text-sm font-semibold">
                    <Clock className="w-3.5 h-3.5" />
                    Aujourd'hui sur {chantier?.reference_chantier || chantier?.nom_chantier || "ce chantier"}
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      id="heuresJour1Rot"
                      type="number"
                      value={heuresJour1Rotation}
                      onChange={(e) => setHeuresJour1Rotation(parseFloat(e.target.value) || 4)}
                      min={0.5}
                      max={12}
                      step={0.5}
                      className="w-20 h-9"
                    />
                    <span className="text-sm text-muted-foreground">heures</span>
                  </div>
                </div>
              ) : joursOuvresRotation > 1 ? (
                // Plusieurs jours : distinction jour 1 / jours suivants
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                    <Label htmlFor="heuresJour1Rot" className="flex items-center gap-2 text-amber-500 text-sm font-semibold">
                      <Clock className="w-3.5 h-3.5" />
                      Aujourd'hui (jour de rotation) sur {chantier?.reference_chantier || chantier?.nom_chantier || "ce chantier"}
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        id="heuresJour1Rot"
                        type="number"
                        value={heuresJour1Rotation}
                        onChange={(e) => setHeuresJour1Rotation(parseFloat(e.target.value) || 4)}
                        min={0.5}
                        max={12}
                        step={0.5}
                        className="w-20 h-9"
                      />
                      <span className="text-sm text-muted-foreground">heures</span>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
                    <Label htmlFor="heuresSuivantsRot" className="flex items-center gap-2 text-primary text-sm font-semibold">
                      <Clock className="w-3.5 h-3.5" />
                      Jours suivants ({joursOuvresRotation - 1} jour{joursOuvresRotation > 2 ? 's' : ''}) sur {chantier?.reference_chantier || chantier?.nom_chantier || "ce chantier"}
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        id="heuresSuivantsRot"
                        type="number"
                        value={heuresJoursSuivantsRotation}
                        onChange={(e) => setHeuresJoursSuivantsRotation(parseFloat(e.target.value) || 8)}
                        min={0.5}
                        max={12}
                        step={0.5}
                        className="w-20 h-9"
                      />
                      <span className="text-sm text-muted-foreground">heures/jour</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Récapitulatif */}
              {joursOuvresRotation > 0 && (
                <div className="flex items-start gap-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg text-xs">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <div className="flex flex-col gap-0.5">
                    <span>Ancien chantier : <strong>{heuresAncienChantier}h</strong> le {format(dateDebutRotation, "dd/MM", { locale: fr })}</span>
                    {joursOuvresRotation === 1 ? (
                      <span>Nouveau chantier : <strong>{heuresJour1Rotation}h</strong> le {format(dateDebutRotation, "dd/MM", { locale: fr })}</span>
                    ) : (
                      <>
                        <span>Nouveau chantier jour 1 : <strong>{heuresJour1Rotation}h</strong></span>
                        <span>Puis : <strong>{heuresJoursSuivantsRotation}h/jour</strong> pendant {joursOuvresRotation - 1} jour{joursOuvresRotation > 2 ? 's' : ''}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRotationModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleRotation}
              disabled={loadingRotation || joursOuvresRotation <= 0}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {loadingRotation ? "Rotation en cours..." : "Confirmer la rotation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de confirmation weekend */}
      <WeekendConfirmDialog
        isOpen={showWeekendDialog}
        onClose={() => {
          setShowWeekendDialog(false);
          setPendingDateFin(null);
        }}
        onConfirm={handleWeekendConfirm}
        dateFin={pendingDateFin || new Date()}
        type={selectedType}
      />

      {/* Modal de sélection des demi-journées */}
      <AlertDialog open={showDayPeriodModal} onOpenChange={setShowDayPeriodModal}>
        <AlertDialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Sélection des jours travaillés
            </AlertDialogTitle>
            <AlertDialogDescription>
              Indiquez précisément quels jours et demi-journées seront travaillés.
              <br />
              <span className="text-primary font-medium">Objectif : {formData.jours_travailles} jour(s)</span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <DayPeriodSelector
            dateDebut={formData.date_debut}
            dateFin={formData.date_fin}
            selectedDays={selectedWorkDays}
            onChange={setSelectedWorkDays}
            expectedTotalDays={formData.jours_travailles}
          />

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDayPeriodModal(false);
              setSelectedWorkDays(new Map());
            }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const totalSelected = calculateTotalSelectedDays(selectedWorkDays);
                // Mettre à jour jours_travailles avec la sélection réelle
                setFormData(prev => ({ ...prev, jours_travailles: totalSelected }));
                setHasConfirmedDaySelection(true);
                setShowDayPeriodModal(false);
              }}
              disabled={selectedWorkDays.size === 0}
            >
              Confirmer ({calculateTotalSelectedDays(selectedWorkDays)} jour{calculateTotalSelectedDays(selectedWorkDays) > 1 ? 's' : ''})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
};

export default AffectationForm;
